import { useCallback, useMemo, useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { useTheme } from '@mui/material';
import { buildSuggestedQueries } from '@realadvisor/constants/source-match';
import type { DeepPartial } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import type { Organisations_Insert_Input } from '@graphql/__generated__/graphql';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import type { RaAddressType } from '@modules/_shared/components/form/RaAddressInput';
import {
  type FormDefinitionType,
  type NonInternalProps,
  RaForm,
} from '@modules/_shared/components/form/RaForm';
import { useNavigateWithSearchParams } from '@modules/_shared/hooks/useNavigateWithSearchParams';
import { useTenantIntegrations } from '@modules/_shared/hooks/useTenantIntegrations';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { OrganisationAccountFields } from '@modules/organisations/components/OrganisationAccountFields';
import {
  CREATE_ORGANISATION,
  GET_ORGANISATIONS_LIST,
} from '@modules/organisations/organisationsQueries';
import type { SourceMatch } from '@modules/teams/components/MatchSuggestionField';
import { useLocale } from '@shared/hooks/locale';
import { isRATenantId } from '@shared/utils/tenant';

type CreateOrganisationFormData = Pick<
  Organisations_Insert_Input,
  'name' | 'website' | 'branding_color' | 'is_lender'
> & {
  address: RaAddressType;
};

export const OrganisationCreateDrawer = () => {
  const navigate = useNavigateWithSearchParams();
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const { breakpoints } = useTheme();
  const { me } = useAppData();
  const showAccountFields =
    me?.is_admin === true && isRATenantId(me.tenant_id ?? '');
  const { hasStripeKey, hasCloseKey } = useTenantIntegrations();
  const [selectedStripe, setSelectedStripe] = useState<SourceMatch[]>([]);
  const [selectedClose, setSelectedClose] = useState<SourceMatch | null>(null);
  const [linkedTenantId, setLinkedTenantId] = useState<string | null>(null);
  const [salesRepUserId, setSalesRepUserId] = useState<string | null>(null);
  const [csmRepUserId, setCsmRepUserId] = useState<string | null>(null);

  const [createOrganisation] = useMutation(CREATE_ORGANISATION, {
    refetchQueries: [GET_ORGANISATIONS_LIST],
    onCompleted: data => {
      if (data?.insert_organisations_one != null) {
        navigate({
          pathname: `../${data.insert_organisations_one.id}/${
            showAccountFields ? 'monitoring' : 'overview'
          }`,
          search: searchParams.toString(),
        });
      }
    },
  });

  const handleClose = () => navigate('../');

  const defaultValues = useMemo<DeepPartial<CreateOrganisationFormData>>(
    () => ({
      address: {},
    }),
    [],
  );

  const addStripeMatch = useCallback((match: SourceMatch) => {
    setSelectedStripe(matches =>
      matches.some(item => item.id === match.id)
        ? matches
        : [...matches, match],
    );
  }, []);

  const organisationFormDefinition = useMemo<
    FormDefinitionType<CreateOrganisationFormData>
  >(
    () =>
      ({ t }) =>
        [
          {
            name: 'details',
            label: t('Details'),
            type: 'category-title',
          },
          {
            name: 'name',
            label: t('Name'),
            type: 'text',
            required: true,
            gridProps: { xs: 12, md: 6 },
          },
          {
            name: 'website',
            label: t('Website URL'),
            type: 'text',
            required: false,
            gridProps: { xs: 12, md: 6 },
          },
          {
            name: 'branding_color',
            label: t('Branding color'),
            type: 'color-picker',
            gridProps: { xs: 12, md: 6 },
          },
          ...(showAccountFields
            ? ([
                {
                  name: 'account-fields',
                  type: 'custom',
                  gridProps: { xs: 12, md: 12 },
                  element: data => (
                    <OrganisationAccountFields
                      suggestedQueries={buildSuggestedQueries(
                        [data.name ?? ''].filter(Boolean),
                        data.website,
                      )}
                      tenantMatchName={data.name ?? ''}
                      hasStripeKey={hasStripeKey}
                      hasCloseKey={hasCloseKey}
                      stripeMatches={selectedStripe}
                      onAddStripe={addStripeMatch}
                      onRemoveStripe={id =>
                        setSelectedStripe(matches =>
                          matches.filter(item => item.id !== id),
                        )
                      }
                      closeMatch={selectedClose}
                      onSelectClose={setSelectedClose}
                      salesRepUserId={salesRepUserId}
                      setSalesRepUserId={setSalesRepUserId}
                      csmRepUserId={csmRepUserId}
                      setCsmRepUserId={setCsmRepUserId}
                      linkedTenantId={linkedTenantId}
                      setLinkedTenantId={setLinkedTenantId}
                    />
                  ),
                },
              ] as ReturnType<FormDefinitionType<CreateOrganisationFormData>>)
            : []),
          {
            name: 'address-title',
            label: t('Address'),
            type: 'category-title',
          },
          {
            label: t('Address'),
            type: 'address',
            gridProps: { xs: 12 },
            path: 'address',
            includeGoogleFields: true,
            requiredFields: () => ({
              route: false,
              postcode: false,
              locality: false,
            }),
          },
        ],
    [
      addStripeMatch,
      csmRepUserId,
      hasCloseKey,
      hasStripeKey,
      linkedTenantId,
      salesRepUserId,
      selectedClose,
      selectedStripe,
      showAccountFields,
    ],
  );

  const onSubmit = useCallback(
    (formData: NonInternalProps<CreateOrganisationFormData>) => {
      const { address, ...organisation } = formData;

      return createOrganisation({
        variables: {
          organisation: {
            ...organisation,
            ...(showAccountFields && {
              stripe_customer_ids: selectedStripe.map(match => match.id),
              close_lead_id: selectedClose?.id ?? null,
              linked_tenant_id: linkedTenantId,
              sales_rep_user_id: salesRepUserId,
              csm_rep_user_id: csmRepUserId,
            }),
            street_number: address.street_number ?? null,
            street: address.route ?? null,
            locality: address.locality ?? null,
            postcode: address.postcode ?? null,
            state: address.state ?? null,
            country: address.country_code ?? null,
            lat: address.lat ?? null,
            lng: address.lng ?? null,
          },
        },
      });
    },
    [
      createOrganisation,
      csmRepUserId,
      linkedTenantId,
      salesRepUserId,
      selectedClose,
      selectedStripe,
      showAccountFields,
    ],
  );

  return (
    <Drawer
      onClose={handleClose}
      title={t('Create organisation')}
      maxWidth={breakpoints.values.md}
    >
      <RaForm
        formDefinition={organisationFormDefinition}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
      />
    </Drawer>
  );
};
